/**
 * Accounting > Vendors — the review queue for what each vendor is.
 *
 * Greg, 2026-10-08: the notes beside a bill were comparing a propane company against the
 * water department, because nothing told the screen what either of them sells. Every
 * vendor here arrives already drafted from two years of QuickBooks; the job on this page
 * is to tick the ones that are right and fix the ones that are not. Biggest spend first,
 * so the first screenful covers most of the money.
 *
 * ⚠️ THE DRAFTING RUNS FROM THIS PAGE, A FEW VENDORS AT A TIME. Claude words "what they
 * sell us" in small batches while the page is open, so no single call runs long enough to
 * time out and there is no background job to go quiet. If Claude is unavailable the page
 * is complete without it: the description simply reads as the account names.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { apVendorProfiles } from '../services/api';
import VendorProfileCard from '../components/VendorProfileCard';
import { FactsList } from '../components/BuyFacts';

const PAGE = 40;

export default function Vendors() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [show, setShow] = useState('todo');
  const [limit, setLimit] = useState(PAGE);
  const [drafting, setDrafting] = useState(null);
  const draftRun = useRef(false);

  const load = useCallback(async () => {
    setErr('');
    try { const d = await apVendorProfiles('list'); setData(d); return d; }
    catch (e) { setErr(e.message || 'Could not load the vendors.'); return null; }
    finally { setLoading(false); }
  }, []);

  // Draft in batches until nothing is left, refreshing the list as the wording arrives.
  const draftAll = useCallback(async (start) => {
    if (draftRun.current || !start) return;
    draftRun.current = true;
    let left = start;
    setDrafting({ left, total: start });
    try {
      for (let i = 0; i < 60 && left > 0; i++) {
        const out = await apVendorProfiles('draft', { limit: 12 });
        if (!out || out.ok === false) break;
        left = out.remaining || 0;
        setDrafting({ left, total: start });
        if (i % 3 === 2 || left === 0) await load();
      }
    } catch (e) { /* the page is complete without the wording */ }
    setDrafting(null);
    draftRun.current = false;
    await load();
  }, [load]);

  useEffect(() => {
    let alive = true;
    load().then((d) => { if (alive && d && d.undrafted > 0) draftAll(d.undrafted); });
    return () => { alive = false; };
  }, [load, draftAll]);

  const onChanged = useCallback((v) => {
    setData((d) => {
      if (!d) return d;
      const vendors = d.vendors.map((x) => (x.key === v.key ? v : x));
      return { ...d, vendors, reviewed: vendors.filter((x) => x.reviewed).length };
    });
  }, []);

  const shown = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    return data.vendors.filter((v) => {
      if (show === 'todo' && v.reviewed) return false;
      if (show === 'done' && !v.reviewed) return false;
      if (!term) return true;
      return [v.name, v.what, ...(v.spellings || []), ...(v.accounts || []).map((a) => a.label)]
        .some((s) => String(s || '').toLowerCase().includes(term));
    });
  }, [data, q, show]);

  const total = data ? data.total : 0;
  const reviewed = data ? data.reviewed : 0;
  const spendAll = data ? data.vendors.reduce((a, v) => a + (v.spent || 0), 0) : 0;
  const spendDone = data ? data.vendors.filter((v) => v.reviewed).reduce((a, v) => a + (v.spent || 0), 0) : 0;
  const pct = spendAll > 0 ? Math.round((spendDone / spendAll) * 100) : 0;

  const tab = (id, label) => (
    <button key={id} className={show === id ? 'btn btn-navy' : 'btn btn-outline'}
      onClick={() => { setShow(id); setLimit(PAGE); }}>{label}</button>
  );

  return (
    <div className="page-content">
      <div className="breadcrumb">
        <span>Accounting</span><span> / </span><span>Vendors</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ color: 'var(--navy)', margin: 0 }}>Vendors</h1>
        <button className="btn btn-ghost" onClick={() => { setLoading(true); load(); }} disabled={loading}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>
      <div style={{ fontSize: 13.5, color: 'var(--gray-600, #4b5563)', marginBottom: 14, maxWidth: 760 }}>
        What each vendor sells us, drafted from two years of QuickBooks. Tick the ones that
        are right and fix the ones that are not. The notes beside a bill in Approvals are
        built from these.
      </div>

      {data && (
        <div className="card" style={{ padding: 14, marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', fontSize: 13.5 }}>
            <span><strong>{reviewed} of {total}</strong> vendors confirmed</span>
            <span style={{ color: 'var(--gray-600, #4b5563)' }}>{pct}% of two-year spend covered</span>
          </div>
          <div style={{ height: 8, background: 'var(--gray-200, #e5e7eb)', borderRadius: 4, marginTop: 8, overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: '#1c7c43' }} />
          </div>
          {drafting && (
            <div style={{ fontSize: 12.5, color: 'var(--gray-600, #4b5563)', marginTop: 8 }}>
              Claude is wording the descriptions: {drafting.total - drafting.left} of {drafting.total} done.
              You can start reviewing now.
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {tab('todo', `To Review${data ? ` (${total - reviewed})` : ''}`)}
        {tab('done', `Confirmed${data ? ` (${reviewed})` : ''}`)}
        {tab('all', 'All')}
        {tab('facts', 'Product Facts')}
        <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 340 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--gray-500, #6b7280)' }} />
          <input className="form-input" value={q} placeholder="Find a vendor, an account or a product"
            onChange={(e) => { setQ(e.target.value); setLimit(PAGE); }}
            style={{ width: '100%', paddingLeft: 30 }} />
        </div>
      </div>

      {err && <div className="card" style={{ padding: 14, color: '#b42318', marginBottom: 12 }}>{err}</div>}
      {loading && !data && <div style={{ color: 'var(--gray-500, #6b7280)' }}>Reading two years of bills from QuickBooks…</div>}

      {show === 'facts' && <FactsList />}

      {show !== 'facts' && data && shown.length === 0 && (
        <div className="card" style={{ padding: 20, color: 'var(--gray-600, #4b5563)' }}>
          {show === 'todo' && !q ? 'Every vendor is confirmed.' : 'No vendors match that.'}
        </div>
      )}

      {show !== 'facts' && shown.slice(0, limit).map((v) => (
        <VendorProfileCard key={v.key} vendor={v} onChanged={onChanged} />
      ))}

      {show !== 'facts' && shown.length > limit && (
        <div style={{ textAlign: 'center', margin: '8px 0 24px' }}>
          <button className="btn btn-outline" onClick={() => setLimit((n) => n + PAGE)}>
            Show {Math.min(PAGE, shown.length - limit)} More ({shown.length - limit} left)
          </button>
        </div>
      )}
    </div>
  );
}
